import React, { useState, useEffect, useRef } from "react";
import { BiLeftArrow, BiRightArrow } from "react-icons/bi";
import PerspectiveHoverWrapper from "../components/PerspectiveHoverWrapper";

const AboutSection3 = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  // Responsive slide shift percent: desktop stays 20%, phones/tablets increase
  const [slideShiftPercent, setSlideShiftPercent] = useState(20);
  // Track which card is "active" (opened via tap/click) for mobile
  const [activeId, setActiveId] = useState(null);
  // Whether click/tap activation is enabled (phones/tablets only)
  const [isClickEnabled, setIsClickEnabled] = useState(false);

  // Sample Digicots images - in real implementation, these would be your actual images
  const slides = [
    {
      id: 1,
      alt: "Cyberpunk Digicots 1",
      image: "/new-final-data/about/slider/Kunal.webp",
      h3: "Founder & CEO",
      name: "Kunal Jaggi",
      description:
        "Built the agency from scratch with a passion for creative disruption.",
    },
    {
      id: 2,
      alt: "Cyberpunk Digicots 2",
      image: "/new-final-data/about/slider/Srishti.webp",
      h3: "Director",
      name: "Srishti Khatri",
      description: "Keeps the team aligned, inspired, and focused on growth.",
    },
    {
      id: 3,
      alt: "Cyberpunk Digicots 3",
      image: "/new-final-data/about/slider/Moulik.webp",
      h3: " Brand Manager",
      name: "Moulik Saxena",
      description: "Shapes powerful brand strategies that deliver real impact.",
    },
    {
      id: 4,
      alt: "Cyberpunk Digicots 4",
      image: "/new-final-data/about/slider/Rimli.webp",
      h3: " Sales Lead",
      name: "Rimli Lahon",
      description:
        "Drives growth by building strong client relationships and closing deals.",
    },
    {
      id: 5,
      alt: "Cyberpunk Digicots 5",
      image: "/new-final-data/about/slider/Pratham.webp",
      h3: "Tech Lead",
      name: "Pratham Meena",
      description:
        "Leads the tech vision, creating seamless and scalable digital solutions",
    },
  ];

  const cardRef = useRef([]);

  // Close active overlay with Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setActiveId(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Auto-slide effect
  useEffect(() => {
    if (!isAutoSliding) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoSliding, slides.length]);

  // Adjust slide shift based on viewport width
  useEffect(() => {
    const updateShift = () => {
      const w = window.innerWidth;
      // xl breakpoint ~1280px (Tailwind default). Keep desktop same (20%).
      // For tablets/phones, increase to slide more per step.
      if (w >= 1280) {
        setSlideShiftPercent(20);
        setIsClickEnabled(false); // desktop: hover-only
        setActiveId(null); // clear any active overlay when moving to desktop
      } else if (w >= 768) {
        // tablets
        setSlideShiftPercent(52); // tuned per user change
        setIsClickEnabled(true);
      } else {
        // phones
        setSlideShiftPercent(55); // tuned per user change
        setIsClickEnabled(true);
      }
    };

    updateShift();
    window.addEventListener("resize", updateShift);
    return () => window.removeEventListener("resize", updateShift);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoSliding(false);
    // Resume auto-sliding after 8 seconds
    setTimeout(() => setIsAutoSliding(true), 8000);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoSliding(false);
    setTimeout(() => setIsAutoSliding(true), 8000);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoSliding(false);
    setTimeout(() => setIsAutoSliding(true), 8000);
  };

  // Toggle active card for mobile/tap interaction
  const toggleActive = (id) => {
    if (!isClickEnabled) return; // ignore clicks on desktop
    setActiveId((prev) => (prev === id ? null : id));
    // Pause auto sliding briefly after manual interaction
    setIsAutoSliding(false);
    setTimeout(() => setIsAutoSliding(true), 8000);
  };

  const onCardKeyDown = (e, id) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleActive(id);
    }
  };

  return (
    <div className="relative w-full pointer-events-auto mx-auto min-h-screen xl:px-35 px-10 dark:bg-zinc-900 pt-20">
      {/* Header */}
      <div className="z-20 mb-10">
        <div className="dark:text-gray-400 text-xs md:text-sm font-medium tracking-wider uppercase mb-2">
          THE TEAM
        </div>
        <h1 className="dark:text-white text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold leading-tight">
          Don't panic, we are
          <br className="hidden xl:block" />
          as normal as you are.
        </h1>
      </div>

      {/* Main Slider Container */}
      <div className="relative overflow-hidden py-10">
        <div
          className="flex h-fit transition-transform duration-700 ease-in-out gap-5"
          style={{
            transform: `translateX(-${currentSlide * slideShiftPercent}%)`,
          }}
        >
          {slides.map((slide, index) => {
            return (
              <div
                key={`${slide.id}-${index}`}
                className="xl:w-[27%] w-[50%] h-full relative flex-shrink-0 perspective-1000"
              >
                <PerspectiveHoverWrapper
                  maxRotation={10}
                  glowEffect={true}
                  className="transform-gpu"
                >
                  <div className="transition-all duration-300 ease-out">
                    <div
                      className="w-full h-full relative overflow-hidden group"
                      {...(isClickEnabled
                        ? {
                            role: "button",
                            tabIndex: 0,
                            "aria-expanded": activeId === slide.id,
                            onClick: () => toggleActive(slide.id),
                            onKeyDown: (e) => onCardKeyDown(e, slide.id),
                          }
                        : {})}
                    >
                      <img
                        src={slide.image}
                        alt={slide.alt}
                        className="w-full h-full object-cover rounded-md"
                      />

                      {/* Cyberpunk overlay effect */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                      {/* Tech UI elements overlay */}
                      <div
                        // style={{ background: 'linear-gradient(90deg, #d90a2c4d 0%, #7646dd42 100%)' }}
                        className={`absolute text-right top-0 left-0 w-full h-full right-4 bg-black/40 transition-opacity duration-300 flex flex-col justify-between items-end p-3 sm:p-4 md:p-5 text-white ${
                          isClickEnabled && activeId === slide.id
                            ? "opacity-80"
                            : "opacity-0"
                        } group-hover:opacity-80`}
                      >
                        <div
                          className={`duration-300 transform ${
                            isClickEnabled && activeId === slide.id
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 -translate-y-2"
                          } group-hover:opacity-100 group-hover:translate-y-0`}
                        >
                          <h3 className="uppercase text-zinc-300 text-xs sm:text-sm md:text-base font-bold">
                            {slide.h3}
                          </h3>
                          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">{slide.name}</h1>
                        </div>
                        <div
                          className={`duration-300 transform ${
                            isClickEnabled && activeId === slide.id
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 translate-y-2"
                          } group-hover:opacity-100 group-hover:translate-y-0`}
                        >
                          <p className="text-sm sm:text-base md:text-lg leading-relaxed">
                            {slide.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </PerspectiveHoverWrapper>
              </div>
            );
          })}
          {slides.map((slide, index) => {
            return (
              <div
                key={`dup-${slide.id}-${index}`}
                className="xl:w-[27%] w-[50%] h-full relative flex-shrink-0 perspective-1000"
              >
                <PerspectiveHoverWrapper
                  maxRotation={10}
                  glowEffect={true}
                  className="transform-gpu"
                >
                  <div className="transition-all duration-300 ease-out">
                    <div
                      className="w-full h-full relative overflow-hidden group"
                      {...(isClickEnabled
                        ? {
                            role: "button",
                            tabIndex: 0,
                            "aria-expanded": activeId === slide.id,
                            onClick: () => toggleActive(slide.id),
                            onKeyDown: (e) => onCardKeyDown(e, slide.id),
                          }
                        : {})}
                    >
                      <img
                        src={slide.image}
                        alt={slide.alt}
                        className="w-full h-full object-cover rounded-md"
                      />

                      {/* Cyberpunk overlay effect */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                      {/* Tech UI elements overlay */}
                      <div
                        // style={{ background: 'linear-gradient(90deg, #d90a2c4d 0%, #7646dd42 100%)' }}
                        className={`absolute text-right top-0 left-0 w-full h-full right-4 bg-black/40 transition-opacity duration-300 flex flex-col justify-between items-end p-3 sm:p-4 md:p-5 text-white ${
                          isClickEnabled && activeId === slide.id
                            ? "opacity-80"
                            : "opacity-0"
                        } group-hover:opacity-80`}
                      >
                        <div
                          className={`duration-300 transform ${
                            isClickEnabled && activeId === slide.id
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 -translate-y-2"
                          } group-hover:opacity-100 group-hover:translate-y-0`}
                        >
                          <h3 className="uppercase text-zinc-300 text-xs sm:text-sm md:text-base font-bold">
                            {slide.h3}
                          </h3>
                          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">{slide.name}</h1>
                        </div>
                        <div
                          className={`duration-300 transform ${
                            isClickEnabled && activeId === slide.id
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 translate-y-2"
                          } group-hover:opacity-100 group-hover:translate-y-0`}
                        >
                          <p className="text-sm sm:text-base md:text-lg leading-relaxed">
                            {slide.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </PerspectiveHoverWrapper>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-between w-full flex-col md:flex-row md:items-center gap-5  xl:gap-0">
        {/* Navigation Dots */}
        <div className="z-20 flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 ${
                index === currentSlide
                  ? "w-8 h-2 bg-[#EE005B] rounded-full"
                  : "w-2 h-2 bg-gray-500 hover:bg-gray-400 rounded-full"
              }`}
            />
          ))}
        </div>

        <div className="flex gap-3">
          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="w-12 h-12 bg-[#EE005B] hover:bg-[#EE005B] transition-colors duration-200 rounded-full flex items-center justify-center shadow-lg"
          >
            <BiLeftArrow className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={goToNext}
            className="w-12 h-12 bg-[#EE005B] hover:bg-[#EE005B] transition-colors duration-200 rounded-full flex items-center justify-center shadow-lg"
          >
            <BiRightArrow className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Cyberpunk grid overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              "linear-gradient(rgba(34, 211, 238, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 211, 238, 0.1) 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        ></div>
      </div>
    </div>
  );
};

export default AboutSection3;
